import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { trackPageView } from '@/shared/utils/analytics/tracker';

// Map route patterns to entity types
const ENTITY_ROUTE_PATTERNS: { pattern: RegExp; entityType: string }[] = [
  { pattern: /^\/articles\/([^/]+)$/, entityType: 'article' },
  { pattern: /^\/brands\/([^/]+)$/, entityType: 'brand' },
  { pattern: /^\/suppliers\/([^/]+)$/, entityType: 'supplier' },
  { pattern: /^\/ingredients\/([^/]+)$/, entityType: 'ingredient' },
];

/**
 * Hook that automatically tracks page views on every route change.
 * Place once in your app, near the router root.
 *
 * For entity detail pages (articles, brands, suppliers, ingredients),
 * you should call `trackEntityView()` directly from the detail page
 * once you've resolved the slug → ID, so the event includes the entity_id.
 *
 * This hook sends a generic page_view for all routes as a baseline.
 */
export function usePageTracking(): void {
  const location = useLocation();
  const previousPath = useRef<string | null>(null);

  useEffect(() => {
    // Avoid duplicate tracking on same path
    if (location.pathname === previousPath.current) return;
    previousPath.current = location.pathname;

    // Check if this is an entity detail page (slug-based, no ID yet)
    const isEntityPage = ENTITY_ROUTE_PATTERNS.some(r => r.pattern.test(location.pathname));

    // Push to GTM dataLayer after a microtask so SEOHead's useLayoutEffect has
    // already updated document.title — GTM reads the title from document.title.
    setTimeout(() => {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'page_view',
        page_path: location.pathname,
        page_title: document.title,
      });
    }, 0);

    // For entity pages, we send a basic page_view here.
    // The detail page component should call trackEntityView() with the resolved ID.
    trackPageView(location.pathname);

    // If NOT an entity page, that's all we need — the generic page_view is enough.
    // Entity detail pages will fire a second, enriched event via trackEntityView().
    void isEntityPage;
  }, [location.pathname]);
}
